import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { resolveMockForRpc } from "@redotech/rpc-mock/mock-registry";
import {
  clearMockResponse,
  getMockResponse,
  setMockResponse,
} from "@redotech/rpc-mock/mock-storage";
import { memo, useCallback, useEffect, useState } from "react";
import * as css from "./rpc-detail-editor.module.css";

function stringify(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function getSource(name: string): string {
  const custom = getMockResponse(name);
  if (custom !== undefined) return "custom";
  return "auto-generated";
}

interface Props {
  name: string;
  onClose: () => void;
}

export const RpcDetailEditor = memo(function RpcDetailEditor({
  name,
  onClose,
}: Props) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState(() => getSource(name));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void resolveMockForRpc(name).then((value) => {
      setText(stringify(value));
      setSource(getSource(name));
      setLoading(false);
    });
  }, [name]);

  const handleSave = useCallback(() => {
    try {
      const parsed: unknown = JSON.parse(text);
      setMockResponse(name, parsed);
      setSource("custom");
      setError(null);
      onClose();
    } catch {
      setError("Invalid JSON");
    }
  }, [name, text, onClose]);

  const handleReset = useCallback(() => {
    clearMockResponse(name);
    void resolveMockForRpc(name).then((value) => {
      setText(stringify(value));
      setSource(getSource(name));
      setError(null);
    });
  }, [name]);

  if (loading) {
    return (
      <div className={css.editor}>
        <span className={css.source}>Loading…</span>
      </div>
    );
  }

  return (
    <div className={css.editor}>
      <span className={css.source}>Source: {source}</span>
      <textarea
        className={`${css.textarea}${error ? ` ${css.textareaError}` : ""}`}
        onChange={(e) => {
          setText(e.target.value);
          setError(null);
        }}
        spellCheck={false}
        value={text}
      />
      {error && <span className={css.error}>{error}</span>}
      <div className={css.actions}>
        <RedoButton
          hierarchy="primary"
          onClick={handleSave}
          size="sm"
          text="Save"
        />
        <RedoButton
          hierarchy="secondary"
          onClick={handleReset}
          size="sm"
          text="Reset to auto"
        />
        <RedoButton
          hierarchy="tertiary"
          onClick={onClose}
          size="sm"
          text="Cancel"
        />
      </div>
    </div>
  );
});
